import React, { useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, generatePath } from 'react-router-dom';
import { hrefObjectToString, queryToSearch } from 'domain/router/helpers';
import ROUTES_PATH from 'domain/router/routesPathConfig';
import { isPublishFailedCategory, isToApproveCategory } from 'domain/categories/helpers';
import { useIntl } from 'react-intl';
import { querySelector } from 'domain/router';
import * as ACL from 'domain/restriction';
import { LayoutCompanyContext } from 'components/mui/Layouts/Company/LayoutCompanyContext';
import CompanyWorkspaceContext from 'pages/company/WorkspaceRoute/CompanyWorkspaceContext';

import { ListItemIcon } from 'components/mui/Layouts/components/Drawer';
import ListItemText from '@mui/material/ListItemText';
import Categories from 'pages/company/components/CompanyNavigation/Categories';
import { useDoctypePersistence } from 'pages/company/WorkspaceRoute/hooks/useDoctypePersistence';
import {
  DropTarget,
  ListItem,
  ListItemButton,
  ListItemImage,
} from 'pages/company/components/CompanyNavigation/Categories/Node/StyledComponents';
import RouterNavLink from 'components/mui/Router/RouterNavLink';

import cx from 'classnames';

const mapStateToProps = (state) => ({
  isGranted: ACL.isGranted(state),
  query: querySelector(state),
});

const Item = ({
  item, // Record, dont try destraction
  level,
  tree,
  onDocMove,
  onCategoryClick,
  onRestoreDocuments,
  isTrashCategory,
}) => {
  const { formatMessage } = useIntl();
  const [isDndOver, setDndOver] = useState();
  const { toggle } = useContext(LayoutCompanyContext);
  const { companyType } = useContext(CompanyWorkspaceContext);
  const { isGranted, query } = useSelector(mapStateToProps);
  const { companyId, category1, category2, category3 } = useParams();
  const resolveDoctypeSegment = useDoctypePersistence();

  const isPendingApproval = isToApproveCategory(item);
  const isPublishFailed = isPublishFailedCategory(item);
  const isAccountant = isGranted(ACL.IS_ACCOUNT);
  const routePath =
    companyType === 'confidential'
      ? ROUTES_PATH.COMPANY_CONFIDENTIAL_WORKSPACE.absolute
      : ROUTES_PATH.COMPANY_WORKSPACE.absolute;

  const hasChildren = tree.size > 0;
  // DA-19681 (da-19295 D10): when a document-type tab is active, carry it across navigation by
  // appending this destination's resolved Financial/General tab id as the next-deeper URL segment.
  // Tab-less categories / All → undefined → plain base link. Additive, explicit-fields only.
  const doctypeSegment = resolveDoctypeSegment(item.tabs);
  const categoryLevels = item.parentList.push(item.id);
  const withDoctype = doctypeSegment ? categoryLevels.push(doctypeSegment) : categoryLevels;

  // DA-19681: highlight this node when its category chain is a prefix of the URL segments. Own
  // matcher — NOT NavLink's isActive, which compares the full href: the appended doctype tail has a
  // per-category id (Financial in category A ≠ Financial in category B), so NavLink's prefix match
  // drops ancestor highlighting. A node's chain holds only real sidebar categories (the doctype tab
  // is never a sidebar node), so any deeper URL segment is harmless. Architecture-agnostic: no
  // knowledge of the doctype tab's presence or position.
  const urlSegments = [category1, category2, category3].filter(Boolean);
  const isCategoryActive =
    categoryLevels.size <= urlSegments.length &&
    categoryLevels.every((id, i) => String(id) === String(urlSegments[i]));

  const path = generatePath(routePath, {
    companyId,
    // @to-do refactor to pass only present parents
    category1: withDoctype.get(0),
    category2: withDoctype.get(1),
    category3: withDoctype.get(2),
  });

  const linkTo = hrefObjectToString({
    pathname: path,
    search: `${queryToSearch({
      ...query,
      tags: [],
    })}`,
  });

  const allowToMove = String(category1) !== String(item.id) && item.documentMovable && isAccountant;

  const onDndDrop = (dndParams) => {
    if (allowToMove && item.id) {
      const { id: categoryId } = item;
      const action = isTrashCategory ? onRestoreDocuments : onDocMove;

      const payload = {
        categoryId,
        ...dndParams,
      };
      if (isTrashCategory) {
        payload.documentIDs = dndParams.documentID;
      }
      action(payload);
      setDndOver(false);
    }
  };

  const onDragEnter = () => {
    if (allowToMove) {
      setDndOver(true);
    }
  };

  const onDragLeave = () => {
    setDndOver(false);
  };

  const onClick = (e) => {
    onCategoryClick(e, item);
  };

  return (
    <>
      <ListItem
        component={DropTarget}
        onDragOver={onDragEnter}
        onDndDrop={onDndDrop}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        sx={{ justifyContent: toggle ? 'flex-start' : 'center' }}
      >
        <ListItemButton
          className={cx({
            'Mui-selected': isCategoryActive,
            isDndOver: isDndOver && allowToMove,
            'is-pending-approval': isPendingApproval && item.count > 0,
            'is-publish-failed': isPublishFailed && item.count > 0,
          })}
          component={RouterNavLink}
          href={linkTo}
          onClick={onClick}
          level={level}
          dense
          activeClassName=""
          sx={{ flexGrow: toggle ? 1 : 0 }}
        >
          {item.icon && (
            <ListItemIcon sx={{ mr: toggle ? 1 : 0 }}>
              <ListItemImage src={item.icon} />
            </ListItemIcon>
          )}
          <ListItemText
            disableTypography
            sx={{
              display: toggle ? 'flex' : 'none',
              whiteSpace: 'normal',
            }}
          >
            {formatMessage({
              id: `category.name.${item.nameLangId}`,
              defaultMessage: item.name,
            })}
            &nbsp;({item.count})
          </ListItemText>
        </ListItemButton>
      </ListItem>
      {hasChildren && level <= 2 && toggle && (
        <Categories
          tree={tree}
          onCategoryClick={onCategoryClick}
          onDocMove={onDocMove}
          onRestoreDocuments={onRestoreDocuments}
          isTrashCategory={isTrashCategory}
        />
      )}
    </>
  );
};

export default Item;
